import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { useBasePath } from '@/lib/paths'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { DashboardPageProps, PhysicalMailDetailed, BusinessRef } from '@/types'
import { Package, Star, Archive, Scissors, Lock } from 'lucide-react'

type Props = {
  business: BusinessRef
  physical_mail: PhysicalMailDetailed
  service_active: boolean
}

export default function BusinessPhysicalMailsShow({ business, physical_mail: mail, service_active }: DashboardPageProps<Props>) {
  const basePath = useBasePath()
  const confirm = useConfirm()
  const [shredDialogOpen, setShredDialogOpen] = useState(false)
  const [shredNotes, setShredNotes] = useState('')

  function toggleArchive() {
    const path = `${basePath}/businesses/${business.id}/physical_mails/${mail.id}/archive`
    mail.archived ? router.delete(path) : router.post(path)
  }

  function toggleFavourite() {
    const path = `${basePath}/businesses/${business.id}/physical_mails/${mail.id}/favourite`
    mail.favourited ? router.delete(path) : router.post(path)
  }

  function submitShredRequest() {
    router.post(`${basePath}/businesses/${business.id}/physical_mails/${mail.id}/shred_request`, {
      shred_request: { notes: shredNotes },
    })
    setShredDialogOpen(false)
  }

  async function cancelShredRequest() {
    const ok = await confirm({
      title: 'Cancel shred request',
      description: 'Cancel the shred request for this mail?',
      confirmLabel: 'Cancel request',
      destructive: true,
    })
    if (ok) {
      router.delete(`${basePath}/businesses/${business.id}/physical_mails/${mail.id}/shred_request`)
    }
  }

  const hasDetails = !!(mail.folder_name || mail.shred_request_notes)

  return (
    <DashboardLayout
      title={mail.sender}
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: business.name, href: `businesses/${business.id}` },
        { label: 'Physical Mail', href: `businesses/${business.id}/physical_mails` },
        { label: mail.sender },
      ]}
    >
      <Head title={`Mail from ${mail.sender}`} />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-muted-foreground" />
              <h1 className="text-xl font-semibold tracking-tight">{mail.sender}</h1>
            </div>
            {mail.subject && (
              <p className="text-sm text-muted-foreground">{mail.subject}</p>
            )}
            {mail.received_on && (
              <p className="text-xs text-muted-foreground mt-0.5">Received: {mail.received_on}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {mail.shredded ? (
              <Badge className="bg-destructive text-destructive-foreground font-normal">Shredded</Badge>
            ) : mail.shred_requested ? (
              <Badge className="bg-secondary text-secondary-foreground font-normal">Shred requested</Badge>
            ) : null}

            {service_active && (
              <>
                <Button size="sm" variant="outline" onClick={toggleFavourite}>
                  <Star className="w-3.5 h-3.5 mr-1.5" fill={mail.favourited ? 'currentColor' : 'none'} />
                  {mail.favourited ? 'Unfavourite' : 'Favourite'}
                </Button>
                <Button size="sm" variant="outline" onClick={toggleArchive}>
                  <Archive className="w-3.5 h-3.5 mr-1.5" />
                  {mail.archived ? 'Unarchive' : 'Archive'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Scan */}
        {mail.scanned && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Scan</h2>
            </div>
            <div className="px-4 py-4">
              {service_active ? (
                mail.scan_url ? (
                  <a
                    href={mail.scan_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    View scan →
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground">Processing…</p>
                )
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm blur-sm select-none text-foreground">View scan document</span>
                  <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              )}
            </div>
          </section>
        )}

        {/* Details */}
        {hasDetails && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Details</h2>
            </div>
            <div className="divide-y">
              {mail.folder_name && (
                <div className="flex items-center px-4 py-3">
                  <span className="text-xs text-muted-foreground w-28 shrink-0">Folder</span>
                  <span className="text-sm text-foreground">{mail.folder_name}</span>
                </div>
              )}
              {mail.shred_request_notes && (
                <div className="flex items-center px-4 py-3">
                  <span className="text-xs text-muted-foreground w-28 shrink-0">Shred notes</span>
                  <span className="text-sm text-foreground">{mail.shred_request_notes}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Actions */}
        {service_active && !mail.shredded && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Actions</h2>
            </div>
            <div className="px-4 py-4 flex flex-wrap gap-2">
              {!mail.shred_requested && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-destructive/50 text-destructive hover:bg-destructive/10"
                  onClick={() => setShredDialogOpen(true)}
                >
                  <Scissors className="w-3.5 h-3.5 mr-1.5" />
                  Request shredding
                </Button>
              )}
              {mail.shred_requested && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-destructive/50 text-destructive hover:bg-destructive/10"
                  onClick={cancelShredRequest}
                >
                  Cancel shred request
                </Button>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Shred request dialog */}
      <Dialog open={shredDialogOpen} onOpenChange={setShredDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request shredding</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="shred-notes">Notes (optional)</Label>
            <Input
              id="shred-notes"
              value={shredNotes}
              onChange={(e) => setShredNotes(e.target.value)}
              placeholder="Any notes for the shred request…"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShredDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={submitShredRequest}>Request shredding</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
